/**
 * Shared edit-state hook for action cards.
 *
 * Manages isEditing / isDirty / isSaving state, save/cancel/discard
 * plumbing via mutable refs, ExplicitSaveTracking integration, and the
 * discard-confirmation guard.
 *
 * Used by both ActionAccordion (accordion path) and
 * MixedActionExpandedRow (table expanded-row path) to keep edit
 * behaviour identical and avoid drift.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { ActionEditContextValue } from './ActionAccordion';
import { SaveValidationError } from './shared/SaveValidationError';
import { useExplicitSaveTracking } from './shared/useExplicitSaveTracking';

interface UseActionEditStateOptions {
  actionId: number;
  showEditButton: boolean;
}

interface UseActionEditStateResult {
  /** The context value to pass to ActionEditContext.Provider */
  editContextValue: ActionEditContextValue;
  /** Whether edit mode is on (regardless of showEditButton) */
  isEditing: boolean;
  /** Whether the user is actively editing (isEditing && showEditButton) */
  activelyEditing: boolean;
  /** Whether the form has unsaved changes */
  isDirty: boolean;
  /** Whether the save operation is in progress */
  isSaving: boolean;
  /** Whether the discard-confirmation dialog should be shown */
  showDiscardConfirm: boolean;
  /** Toggle edit mode (or prompt discard if dirty) */
  handleToggleEdit: () => void;
  /** Save handler — calls the form's registered save handler */
  handleSave: () => Promise<void>;
  /** Cancel handler — prompts discard if dirty */
  handleCancel: () => void;
  /** Confirm discard — resets all edit state */
  performDiscard: () => void;
  /** Dismiss the discard dialog */
  handleKeepEditing: () => void;
}

export function useActionEditState({
  actionId,
  showEditButton,
}: UseActionEditStateOptions): UseActionEditStateResult {
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const saveHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const cancelHandlerRef = useRef<(() => void) | null>(null);
  const discardRef = useRef<(() => void) | undefined>(undefined);

  const explicitSaveTracking = useExplicitSaveTracking();

  useEffect(() => {
    if (!explicitSaveTracking) {
      return;
    }
    explicitSaveTracking.reportDirty(actionId, isEditing && isDirty);
    return () => {
      explicitSaveTracking.reportDirty(actionId, false);
    };
  }, [explicitSaveTracking, actionId, isEditing, isDirty]);

  useEffect(() => {
    if (!explicitSaveTracking || !isEditing) {
      return;
    }
    return explicitSaveTracking.registerCancel(actionId, () => {
      discardRef.current?.();
    });
  }, [explicitSaveTracking, actionId, isEditing]);

  const handleSetDirty = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  const handleSave = useCallback(async () => {
    if (!saveHandlerRef.current) {
      return;
    }
    setIsSaving(true);
    try {
      await saveHandlerRef.current();
      setIsDirty(false);
      setIsEditing(false);
    } catch (err) {
      if (err instanceof SaveValidationError) {
        // Client-side validation failed. The form is already surfacing
        // inline field-level errors, so keep the editor open and skip the
        // generic save-failure toast (which would be misleading here).
        return;
      }
      console.error('Failed to save action:', err);
      toast.error("We couldn't save the changes. Try again.");
    } finally {
      setIsSaving(false);
    }
  }, []);

  const performDiscard = useCallback(() => {
    cancelHandlerRef.current?.();
    setIsDirty(false);
    setIsEditing(false);
    setShowDiscardConfirm(false);
  }, []);

  discardRef.current = performDiscard;

  const handleKeepEditing = useCallback(() => setShowDiscardConfirm(false), []);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      performDiscard();
    }
  }, [isDirty, performDiscard]);

  const handleToggleEdit = useCallback(() => {
    if (isEditing && isDirty) {
      setShowDiscardConfirm(true);
    } else if (isEditing) {
      performDiscard();
    } else {
      setIsDirty(false);
      setIsEditing(true);
    }
  }, [isEditing, isDirty, performDiscard]);

  // Click-to-edit entry from inside the card content. Idempotent: re-entering
  // while already editing must NOT reset the dirty flag, or clicking a second
  // field would silently disarm the Save button.
  const startEditing = useCallback(() => {
    if (isEditing) {
      return;
    }
    setIsDirty(false);
    setIsEditing(true);
  }, [isEditing]);

  const editContextValue = useMemo<ActionEditContextValue>(
    () => ({
      isEditing: isEditing && showEditButton,
      isDirty,
      // Gated on showEditButton for the same reason isEditing is: an action
      // the shell won't offer a Save button for must not be click-editable.
      startEditing: showEditButton ? startEditing : null,
      setDirty: handleSetDirty,
      saveHandlerRef,
      cancelHandlerRef,
    }),
    [isEditing, showEditButton, isDirty, handleSetDirty, startEditing]
  );

  const activelyEditing = isEditing && showEditButton;

  return {
    editContextValue,
    isEditing,
    activelyEditing,
    isDirty,
    isSaving,
    showDiscardConfirm,
    handleToggleEdit,
    handleSave,
    handleCancel,
    performDiscard,
    handleKeepEditing,
  };
}
